import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface CategoryIcon {
  icon_type: string;
  icon_name: string;
}

export interface Category {
  id: string;
  name: string;
  description: string | null;
  icon: CategoryIcon;
  icon_url: string | null;
  start_color: string;
  end_color: string;
  display_order: number;
}

interface CategoriesResponse {
  success: boolean;
  message: string;
  data: Category[];
}

interface UseCategoryReturn {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
}

// Cache for categories
let cachedCategories: Category[] = [];
let lastFetched: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useCategory = (): UseCategoryReturn => {
  const [categories, setCategories] = useState<Category[]>(cachedCategories);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async (): Promise<void> => {
    // Return cached data if still valid
    if (lastFetched && cachedCategories.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetched;
      if (timeSinceLastFetch < CACHE_DURATION) {
        setCategories(cachedCategories);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get<CategoriesResponse>(API_ENDPOINTS.GET_CATEGORIES);

      if (response.data.success) {
        cachedCategories = response.data.data;
        lastFetched = Date.now();
        setCategories(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch categories');
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err.message || 'Failed to fetch categories';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    isLoading,
    error,
    fetchCategories,
  };
};

export default useCategory;
